import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FaHeart, FaFileDownload, FaArrowLeft, FaEye, FaRegClock, FaUserCircle, FaShareAlt } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import { useColorMode, Box, Spinner } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import SafeContent from '../Securite/safeContent';

const BlogDetail = () => {
    const { id } = useParams();
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [liked, setLiked] = useState(false);
    const [shared, setShared] = useState(false);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const navigate = useNavigate();

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    useEffect(() => {
        const fetchBlogDetail = async () => {
            try {
                const [detailRes, allRes] = await Promise.all([
                    axios.get(`${API_URL}/api/blog/${id}`),
                    axios.get(`${API_URL}/api/blog`),
                ]);
                if (detailRes.status === 200) setBlog(detailRes.data);
                if (allRes.status === 200) {
                    const others = allRes.data
                        .filter(b => String(b.id) !== String(id))
                        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                        .slice(0, 3);
                    setRelatedBlogs(others);
                }
            } catch (error) {
                console.error('Erreur réseau:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogDetail();
    }, [id, API_URL]);

    useEffect(() => {
        if (!id) return;
        const key = 'his_viewed_articles';
        const viewed = JSON.parse(localStorage.getItem(key) || '[]');
        if (!viewed.includes(id)) {
            axios.patch(`${API_URL}/api/blog/${id}/view`).then(() => {
                localStorage.setItem(key, JSON.stringify([...viewed, id]));
            }).catch(() => {});
        }
    }, [id, API_URL]);

    const handleLikeToggle = async () => {
        try {
            if (liked) {
                await axios.patch(`${API_URL}/api/blog/${id}/unlike`);
                setBlog(prev => ({ ...prev, likes: Math.max(prev.likes - 1, 0) }));
            } else {
                await axios.patch(`${API_URL}/api/blog/${id}/like`);
                setBlog(prev => ({ ...prev, likes: (prev.likes || 0) + 1 }));
            }
            setLiked(!liked);
        } catch (error) {
            console.error('Erreur like:', error);
        }
    };

    if (loading) return (
        <Box h="100vh" display="flex" flexDir="column" alignItems="center" justifyContent="center" bg={isDark ? "#0a0f16" : "#FAFCFF"}>
            <Spinner size="xl" thickness="4px" color="blue-600" />
            <p className="mt-4 text-[10px] font-black uppercase tracking-widest opacity-40">Préparation de votre lecture</p>
        </Box>
    );

    if (!blog) return (
        <Box h="100vh" display="flex" alignItems="center" justifyContent="center" bg={isDark ? "#0a0f16" : "#FAFCFF"}>
            <p className="text-xl font-light italic">Cet article semble avoir disparu...</p>
        </Box>
    );

    return (
        <Box className={isDark ? 'bg-[#0a0f16] text-white' : 'bg-[#FAFCFF] text-[#1B365D]'}>
            <Helmet>
                <title>{blog.title} — HIS</title>
                <meta name="description" content={blog.description} />
                <meta property="og:title" content={`${blog.title} — HIS`} />
                <meta property="og:description" content={blog.description} />
                <meta property="og:image" content={blog.image ? `${API_URL}/${blog.image}` : 'https://www.hisvie.com/logo192.png'} />
                <meta property="og:url" content={`https://www.hisvie.com/blog/${id}`} />
                <meta property="og:type" content="article" />
                <meta property="og:site_name" content="HIS — Hetep Iaout Services" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${blog.title} — HIS`} />
                <meta name="twitter:description" content={blog.description} />
                <meta name="twitter:image" content={blog.image ? `${API_URL}/${blog.image}` : 'https://www.hisvie.com/logo192.png'} />
            </Helmet>

            {/* --- HERO IMAGE (Full Height Impact) --- */}
            <div className="relative w-full h-[70vh] overflow-hidden">
                {blog.image ? (
                    <motion.img
                        initial={{ scale: 1.1 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 1.5 }}
                        src={`${API_URL}/${blog.image}`}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-blue-50'}`}>
                        <span className="text-8xl font-black opacity-5 uppercase tracking-tighter">Éditorial</span>
                    </div>
                )}
                
                {/* Overlay Dégradé */}
                <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0a0f16] via-[#0a0f16]/40' : 'from-[#FAFCFF] via-transparent'} to-transparent`} />

                {/* Bouton Retour Floating */}
                <div className="absolute top-32 left-6 md:left-12 z-20">
                    <button
                        onClick={() => navigate(-1)}
                        className={`group flex items-center gap-3 px-6 py-3 rounded-full backdrop-blur-md border transition-all ${
                            isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/20' : 'bg-black/5 border-black/10 text-[#1B365D] hover:bg-black/10'
                        }`}
                    >
                        <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Retour au blog</span>
                    </button>
                </div>
            </div>

            {/* --- CONTENU DE L'ARTICLE --- */}
            <article className="max-w-4xl mx-auto px-6 -mt-32 relative z-10">
                
                {/* Header Card */}
                <header className="mb-16 space-y-8">
                    <div className="space-y-4">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`text-4xl md:text-7xl font-black tracking-tighter leading-[1.1] ${isDark ? 'text-white' : 'text-[#1B365D]'}`}
                        >
                            {blog.title}
                        </motion.h1>
                        <div className="w-24 h-1.5 bg-blue-600 rounded-full"></div>
                    </div>

                    {/* Meta Infos Badges */}
                    <div className="flex flex-wrap items-center gap-4 text-[10px] font-black uppercase tracking-widest">
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/10 text-blue-400' : 'bg-blue-50 border-blue-100 text-blue-600'}`}>
                            <FaUserCircle size={14} /> Rédaction HIS
                        </div>
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
                            <FaRegClock size={14} /> {new Date(blog.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </div>
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/10 text-gray-400' : 'bg-gray-100 border-gray-200 text-gray-500'}`}>
                            <FaEye size={14} /> {blog.views || 0} vue{(blog.views || 0) !== 1 ? 's' : ''}
                        </div>
                    </div>
                </header>

                {/* Introduction (Serif Italic style) */}
                <div className="mb-16">
                    <p className={`text-xl md:text-3xl font-serif italic font-light leading-relaxed border-l-4 border-blue-600 pl-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {blog.description}
                    </p>
                </div>

                {/* Main Content Body */}
                <div className={`prose prose-xl max-w-none mb-20 leading-relaxed font-light ${isDark ? 'prose-invert text-gray-300' : 'prose-slate text-gray-700'}`}>
                    <SafeContent htmlContent={blog.content} />
                </div>

                {/* --- BLOC RESSOURCE PDF (Design Bento) --- */}
                {blog.pdf && (
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        className={`p-8 md:p-12 rounded-[2.5rem] mb-20 border transition-all ${
                            isDark ? 'bg-gray-900/50 border-gray-800 shadow-2xl' : 'bg-white border-blue-100 shadow-xl shadow-blue-900/5'
                        }`}
                    >
                        <div className="flex flex-col md:flex-row items-center justify-between gap-10">
                            <div className="flex items-center gap-6">
                                <div className="w-20 h-20 rounded-3xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/30">
                                    <FaFileDownload size={32} />
                                </div>
                                <div className="space-y-1 text-center md:text-left">
                                    <h4 className="font-black text-xl tracking-tight">Ressource complémentaire</h4>
                                    <p className="text-[10px] opacity-50 uppercase font-black tracking-widest">Document PDF officiel • HIS</p>
                                </div>
                            </div>
                            
                            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                                <a 
                                    href={`${API_URL}/${blog.pdf}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${
                                        isDark ? 'border-white/10 hover:bg-white/5 text-white' : 'border-black/10 hover:bg-gray-50 text-[#1B365D]'
                                    }`}
                                >
                                    <FaEye /> Consulter
                                </a>
                                <a 
                                    href={`${API_URL}/${blog.pdf}`} 
                                    download
                                    className="flex-1 flex items-center justify-center gap-3 px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all"
                                >
                                    <FaFileDownload /> Télécharger
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* --- FOOTER DE L'ARTICLE (Engagement) --- */}
                <footer className="flex flex-col items-center gap-10 pt-12 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex flex-col items-center gap-6">
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-40">Vous avez aimé cette lecture ?</span>
                        <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={handleLikeToggle}
                            className={`flex items-center gap-4 px-10 py-5 rounded-full transition-all group ${
                                liked
                                ? 'bg-red-500 text-white shadow-2xl shadow-red-500/40'
                                : `border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-black/10 hover:bg-gray-50'} text-gray-500`
                            }`}
                        >
                            <FaHeart size={24} className={liked ? 'scale-125' : 'group-hover:scale-125 transition-transform'} />
                            <span className="font-black text-sm uppercase tracking-tighter">{blog.likes || 0} Appréciations</span>
                        </motion.button>
                    </div>

                    <button
                        onClick={async () => {
                            const url = window.location.href;
                            if (navigator.share) {
                                try {
                                    await navigator.share({ title: blog.title, text: blog.description, url });
                                } catch {}
                            } else {
                                await navigator.clipboard.writeText(url);
                                setShared(true);
                                setTimeout(() => setShared(false), 2500);
                            }
                        }}
                        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity"
                    >
                        <FaShareAlt /> {shared ? 'Lien copié !' : "Partager l'article"}
                    </button>
                </footer>
            </article>

            {/* --- VOUS AIMEREZ AUSSI --- */}
            {relatedBlogs.length > 0 && (
                <section className={`mt-24 pb-24 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                    <div className="max-w-5xl mx-auto px-6 pt-16">
                        <div className="flex items-center gap-4 mb-10">
                            <h2 className={`text-2xl md:text-3xl font-bold tracking-tight shrink-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Vous aimerez aussi
                            </h2>
                            <div className={`flex-1 h-px ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {relatedBlogs.map((related, i) => (
                                <motion.div
                                    key={related.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="group"
                                >
                                    <a
                                        href={`/blog/${related.id}`}
                                        className={`flex flex-col h-full rounded-2xl overflow-hidden border transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}
                                    >
                                        <div className="overflow-hidden" style={{ aspectRatio: '16/9' }}>
                                            <img
                                                src={`${API_URL}/${related.image}`}
                                                alt={related.title}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            />
                                        </div>
                                        <div className="p-5 flex flex-col flex-1">
                                            <span className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                {new Date(related.createdAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                            </span>
                                            <h3 className={`text-base font-bold leading-snug line-clamp-2 mb-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-400' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                                <SafeContent htmlContent={related.title} />
                                            </h3>
                                            <p className={`text-sm line-clamp-2 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                <SafeContent htmlContent={related.description} />
                                            </p>
                                            <div className={`mt-4 pt-3 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                                                <span className={`text-xs font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    Lire l'article →
                                                </span>
                                            </div>
                                        </div>
                                    </a>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </Box>
    );
};

export default BlogDetail;